'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useEffect } from 'react';

// Branded 500-equivalent. Next.js renders this for any unhandled error in
// a route segment. The "Report this" link funnels into /report-bug with
// the digest + truncated message pre-encoded as query params; the bug-
// report page can read them later (Phase 2 enhancement) or ignore them
// silently today — either way the link is safe to ship.
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('[error.tsx]', error);
  }, [error]);

  const reportUrl =
    `/report-bug?context=error` +
    `&digest=${encodeURIComponent(error.digest ?? 'unknown')}` +
    `&message=${encodeURIComponent((error.message ?? '').slice(0, 500))}`;

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--s-6)',
        background: 'var(--surface-0)',
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <div style={{ maxWidth: 480, textAlign: 'center' }}>
        <Image
          src="/indiestrylogofull.png"
          alt="Indiestry"
          width={64}
          height={64}
          priority
          style={{ marginBottom: 'var(--s-6)', display: 'inline-block' }}
        />

        <h1 className="t-display" style={{ marginBottom: 'var(--s-2)' }}>
          Something went wrong
        </h1>
        <p
          className="t-body"
          style={{ color: 'var(--text-secondary)', marginBottom: 'var(--s-2)' }}
        >
          We hit an unexpected error rendering this page.
        </p>
        {error.digest && (
          <p
            className="t-mono"
            style={{
              color: 'var(--text-tertiary)',
              marginBottom: 'var(--s-7)',
            }}
          >
            Error ID: {error.digest}
          </p>
        )}

        <div
          style={{
            display: 'flex',
            gap: 'var(--s-2)',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={reset}
            style={{
              padding: 'var(--s-3) var(--s-5)',
              borderRadius: 'var(--r-sm)',
              background: 'var(--accent)',
              color: 'var(--text-on-accent)',
              fontWeight: 500,
              fontSize: 14,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
          <Link
            href={reportUrl}
            style={{
              padding: 'var(--s-3) var(--s-5)',
              borderRadius: 'var(--r-sm)',
              background: 'var(--surface-3)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-default)',
              fontWeight: 500,
              fontSize: 14,
              display: 'inline-block',
              textDecoration: 'none',
            }}
          >
            Report this
          </Link>
        </div>

        {process.env.NODE_ENV === 'development' && (
          <details style={{ marginTop: 'var(--s-7)', textAlign: 'left' }}>
            <summary
              style={{ color: 'var(--text-tertiary)', cursor: 'pointer' }}
            >
              Error details (dev only)
            </summary>
            <pre
              style={{
                marginTop: 'var(--s-2)',
                fontSize: 12,
                color: 'var(--text-tertiary)',
                background: 'var(--surface-2)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--r-sm)',
                padding: 'var(--s-2)',
                overflow: 'auto',
              }}
            >
              {error.message}
            </pre>
          </details>
        )}
      </div>
    </main>
  );
}
